import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'

import { setLoading, useAnaContext, useAnaDispatch } from 'src/components/providers/AnaProvider'
import { AnaSection } from 'src/screens/Anamnesis/Anamnesis/Anamnesis'

export const getIndices = ({ factoringAgreementMissing }: { factoringAgreementMissing: boolean }) => ({
  [AnaSection.PersonalInformation]: 0,
  [AnaSection.ContactDetails]: 0,
  [AnaSection.Insurance]: 0,
  [AnaSection.AllergiesAndMedications]: 1,
  [AnaSection.Preconditions1]: 1,
  [AnaSection.Preconditions2]: 1,
  [AnaSection.Preconditions3]: 1,
  [AnaSection.Preparations]: 2,
  [AnaSection.Wishes1]: 2,
  [AnaSection.Wishes2]: 2,
  [AnaSection.Wishes3]: 2,
  [AnaSection.Consents]: 2,
  ...(factoringAgreementMissing ? { [AnaSection.FactoringAgreement]: 3 } : {}),
  [AnaSection.Success]: factoringAgreementMissing ? 3 : 2,
})

export enum AnaMode {
  RENEW = 'renew',
  EDIT = 'edit',
  NEW = 'new',
}

export function useAnaNavigation({ mode }: { mode: AnaMode }) {
  const { asPath, push } = useRouter()

  const { factoringAgreementMissing } = useAnaContext()

  const dispatch = useAnaDispatch()

  const indices = getIndices({ factoringAgreementMissing })
  const steps = Object.keys(indices)

  const search = useSearchParams()
  const searchReferenceId = search.get('referenceId')

  // Keep the original query (preserves casing of keys like `historyReferenceId`)
  // so it survives across step navigations.
  const [rawPath, rawQuery] = asPath.split('?')
  const queryString = rawQuery ? `?${rawQuery}` : ''

  const parts = (() => {
    const path = rawPath.toLowerCase()
    const containedSeparator = `/${mode}/`
    const separator = `/${mode}`

    // Legacy flow: the real id rides in the `referenceId` query + a literal
    // 'referenceId' path segment — put it back into the path so middleware
    // re-injects it on the next page. Documents flow (renew/edit from /documents)
    // carries `historyReferenceId` in the query instead and keeps the literal
    // segment, so only substitute when a referenceId query is actually present.
    const normalizedPath = searchReferenceId ? path.replace('referenceid', searchReferenceId) : path

    return normalizedPath.split(path.includes(containedSeparator) ? containedSeparator : separator)
  })()

  const base = parts[0]
  const step = parts[1]?.split('?')[0] || AnaSection.PersonalInformation

  const allDone = step === AnaSection.Success

  const nextStep = steps[steps.indexOf(step) + 1]

  const nextPage = nextStep ? `${base}/${mode}/${nextStep}${queryString}` : '/'

  return {
    step,
    indices,
    allDone,
    goToNextStep: () => {
      dispatch(setLoading(true))
      push(nextPage)
    },
  }
}
